import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createSavedList as createSavedListApi,
  GetAllSavedLists,
  GetListTableData,
  GetListRefreshData,
  GetListSummaryData,
  DeleteList,
} from "../api/marketOverviewApi";
import logger from "../utils/logger";
import { track } from "@/analytics";

const initialState = {
  lists: [],
  currentList: null,
  currentListTableData: null,
  currentListRefreshData: null,
  currentListSummary: null,
  loading: false,
  tableLoading: false,
  refreshLoading: false,
  summaryLoading: false,
  error: null,
};

export const createSavedList = createAsyncThunk(
  "savedList/create",
  async (
    {
      id_client,
      user_id,
      name,
      id_outlet_info,
      applied_filters,
      selected_skus,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await createSavedListApi({
        id_client,
        user_id,
        name,
        id_outlet_info,
        applied_filters,
        selected_skus,
      });
      // Emit list_created to the governed catalog on confirmed success.
      // In the thunk (after the API resolves) so a rejected create never emits.
      const filterCount = Array.isArray(applied_filters)
        ? applied_filters.length
        : applied_filters && typeof applied_filters === "object"
          ? Object.keys(applied_filters).length
          : 0;
      track("list_created", {
        outlet_count: Array.isArray(id_outlet_info) ? id_outlet_info.length : 0,
        has_filters: filterCount > 0,
        filter_count: filterCount,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create saved list");
    }
  }
);

export const fetchSavedLists = createAsyncThunk(
  "savedList/fetchAll",
  async ({ id_client, user_id }, { rejectWithValue }) => {
    try {
      const response = await GetAllSavedLists({ id_client, user_id });

      return response;
    } catch (error) {
      logger.error(error);

      return rejectWithValue(error.message || "Failed to fetch saved lists");
    }
  }
);

export const fetchListTableData = createAsyncThunk(
  "savedList/fetchTableData",
  async ({ id_list, country, id_client, user_id }, { rejectWithValue }) => {
    try {
      const response = await GetListTableData({
        id_list,
        country,
        id_client,
        user_id,
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch list table data"
      );
    }
  }
);

// Per-refresh series for one list. Feeds the trend chart.
export const fetchListRefreshData = createAsyncThunk(
  "savedList/fetchRefreshData",
  async ({ id_list, country, id_client, user_id }, { rejectWithValue }) => {
    try {
      const response = await GetListRefreshData({
        id_list,
        country,
        id_client,
        user_id,
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch list refresh data"
      );
    }
  }
);

export const fetchListSummaryData = createAsyncThunk(
  "savedList/fetchSummaryData",
  async ({ id_list, country, id_client, user_id }, { rejectWithValue }) => {
    try {
      const response = await GetListSummaryData({
        id_list,
        country,
        id_client,
        user_id,
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch list summary data"
      );
    }
  }
);

export const fetchListDetails = createAsyncThunk(
  "savedList/fetchDetails",
  async (
    { id_list, country, id_client, user_id },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const [tableData, refreshData, summaryData] = await Promise.all([
        dispatch(
          fetchListTableData({ id_list, country, id_client, user_id })
        ).unwrap(),
        dispatch(
          fetchListRefreshData({ id_list, country, id_client, user_id })
        ).unwrap(),
        dispatch(
          fetchListSummaryData({ id_list, country, id_client, user_id })
        ).unwrap(),
      ]);

      return {
        id_list,
        tableData,
        refreshData,
        summaryData,
      };
    } catch (error) {
      logger.error(error);
      return rejectWithValue(error.message || "Failed to fetch list details");
    }
  }
);

export const deleteSavedList = createAsyncThunk(
  "savedList/delete",
  async ({ id_client, id_user, id_list }, { rejectWithValue }) => {
    try {
      const response = await DeleteList({ id_client, id_user, id_list });
      // Emit list_deleted to the governed catalog on confirmed success.
      // In the thunk so all three delete UI sites are covered by one emit.
      if (id_list != null) track("list_deleted", { list_id: String(id_list) });
      return { id_list, response };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete saved list");
    }
  }
);

const savedListSlice = createSlice({
  name: "savedList",
  initialState,
  reducers: {
    clearCurrentList: (state) => {
      state.currentList = null;
      state.currentListTableData = null;
      state.currentListRefreshData = null;
      state.currentListSummary = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentList: (state, action) => {
      state.currentList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSavedList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSavedList.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.lists.unshift(action.payload);
        }
      })
      .addCase(createSavedList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchSavedLists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSavedLists.fulfilled, (state, action) => {
        state.loading = false;
        state.lists = action.payload
          ? Object.entries(action.payload).map(([id, listData]) => ({
              id_saved_list: id,
              ...listData,
            }))
          : [];
      })
      .addCase(fetchSavedLists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchListTableData.pending, (state) => {
        state.tableLoading = true;
      })
      .addCase(fetchListTableData.fulfilled, (state, action) => {
        state.tableLoading = false;
        state.currentListTableData = action.payload;
      })
      .addCase(fetchListTableData.rejected, (state, action) => {
        state.tableLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchListRefreshData.pending, (state) => {
        state.refreshLoading = true;
      })
      .addCase(fetchListRefreshData.fulfilled, (state, action) => {
        state.refreshLoading = false;
        state.currentListRefreshData = action.payload;
      })
      .addCase(fetchListRefreshData.rejected, (state, action) => {
        state.refreshLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchListSummaryData.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(fetchListSummaryData.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.currentListSummary = action.payload;
      })
      .addCase(fetchListSummaryData.rejected, (state, action) => {
        state.summaryLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchListDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchListDetails.fulfilled, (state, action) => {
        state.loading = false;
        const { id_list, tableData, refreshData, summaryData } = action.payload;

        state.currentListTableData = tableData;
        state.currentListRefreshData = refreshData;
        state.currentListSummary = summaryData;

        const listIndex = state.lists.findIndex(
          (list) => list.id_saved_list === id_list
        );
        if (listIndex !== -1) {
          state.currentList = {
            ...state.lists[listIndex],
            tableData,
            refreshData,
            summaryData,
          };
        }
      })
      .addCase(fetchListDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteSavedList.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteSavedList.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteSavedList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentList, clearError, setCurrentList } =
  savedListSlice.actions;
export default savedListSlice.reducer;
